import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../apiHelpers';
import RollupChips from '../../ChargeControl/RollupChips';
import ChargeRing from '../../ChargeControl/ChargeRing';
import { aggregateFleet, socHex, prefsToLocationId, locationIdParam } from '../../ChargeControl/chargeUi';
import Panel from '../../components/ui/Panel';
import Alert from '../../components/ui/Alert';
import EmptyState from '../../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import FadeIn from '../../components/ui/FadeIn';
import Reveal from '../../components/ui/Reveal';
import Appear from '../../components/ui/Appear';
import { CATEGORY } from '../../components/ui/categoryColors';
import type { ChargerView, FleetResponse, ChargePrefs, LocationItem, LocationId } from '../../ChargeControl/types';

const POLL_MS = 15000;  // glanceable; the full page polls faster

/** Dashboard widget: status of the user's currently-selected charge location. */
const ChargeStatusModule = () => {
  const api = useApi();
  const [fleet, setFleet] = useState<ChargerView[]>([]);
  const [locId, setLocId] = useState<LocationId>('mine');
  const [locName, setLocName] = useState('My Chargers');
  const [collectorOk, setCollectorOk] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(false);

  // Resolve the current location ONCE — prefs/locations rarely change; the
  // 15s poll below only re-fetches the (changing) fleet.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [prefs, locs] = await Promise.all([
          api.get('/charge/prefs') as Promise<ChargePrefs>,
          api.get('/charge/locations') as Promise<{ locations: LocationItem[] }>,
        ]);
        if (!alive) return;
        const id = prefsToLocationId(prefs.current_location);
        setLocId(id);
        setLocName(id === 'mine'
          ? 'My Chargers'
          : (locs.locations.find((l) => l.id === id)?.name || 'Group'));
      } catch { /* the fleet poll surfaces errors */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll fleet for the resolved scope (My Chargers or a group) — curated only.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = (await api.get(
          `/charge/fleet?location_id=${locationIdParam(locId)}`
        )) as FleetResponse;
        if (!alive) return;
        setFleet(res.chargers || []);
        setCollectorOk(res.collector_ok !== false);
        setErr(false);
        setLoaded(true);
      } catch {
        if (!alive) return;
        setErr(true);
        setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locId]);

  const agg = useMemo(() => aggregateFleet(fleet), [fleet]);

  return (
    <Link
      to="/charge" aria-label={`Open Charge Control: ${locName}`}
      className="block rounded-panel transition-[filter] duration-200 hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <Panel
        title={locName}
        level={3}
        padding="sm"
        className="h-full"
        actions={(
          <>
            {loaded && <span className="text-xs font-normal text-on-surface-variant">{agg.online}/{agg.total} online</span>}
            <span className="text-xs text-primary">Open →</span>
          </>
        )}
      >
      <FadeIn key={!loaded ? 'loading' : err ? 'error' : agg.total === 0 ? 'empty' : 'status'}>
      {!loaded ? (
        <SkeletonGroup label="Loading charge status" className="space-y-3 py-1">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-2/3" />
        </SkeletonGroup>
      ) : err ? (
        <Alert variant="text" tone="error" live={false}>Charge status unavailable.</Alert>
      ) : agg.total === 0 ? (
        <EmptyState variant="line">
          {locId === 'mine' ? 'No chargers pinned yet.' : 'No chargers in this group.'}
        </EmptyState>
      ) : (
        <>
          <Reveal show={!collectorOk}>
            <Alert variant="text" tone="error" className="mb-2">Collector offline. Showing last-known status.</Alert>
          </Reveal>
          <div className="flex items-center gap-3 mb-3">
            <ChargeRing
              pct={agg.rollup.soc_avg} color={socHex(agg.rollup.soc_avg)}
              glow={(agg.rollup.counts.charging || 0) > 0} size={48} bg="#1f2937"
            />
            <div className="text-xs text-on-surface-variant">
              {agg.rollup.present_count} batteries
              <Appear show={agg.rollup.thermal_warnings > 0}>
                <span style={{ color: CATEGORY.amber.ink.oklch }}> · {agg.rollup.thermal_warnings} thermal</span>
              </Appear>
            </div>
          </div>
          <RollupChips rollup={agg.rollup} />
        </>
      )}
      </FadeIn>
      </Panel>
    </Link>
  );
};

export default ChargeStatusModule;
